import Layout from "../Layout";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Cookies from 'js-cookie'; // Import js-cookie
import CryptoJS from 'crypto-js'; // Import crypto-js
import axios from 'axios'; // Import axios

// --- IMPORTANT: Store this key securely, do NOT hardcode in production ---
const SECRET_COOKIE_KEY = "mySuperSecretCookieKey123!@#"; // Must match Register.js and Menu.js

// Helper function for decryption
const decrypt = (ciphertext) => {
  try {
    const bytes = CryptoJS.AES.decrypt(ciphertext, SECRET_COOKIE_KEY);
    return bytes.toString(CryptoJS.enc.Utf8);
  } catch (e) {
    console.error("Error decrypting cookie:", e);
    return null;
  }
};

// Helper function for encryption (added for consistency, though not strictly needed here for cart saving)
const encrypt = (text) => {
  return CryptoJS.AES.encrypt(text, SECRET_COOKIE_KEY).toString();
};

// API Base URL and Authorization Header (reusing from Menu.js)
const API_BASE_URL = 'https://stage.beachcabana.lk/hotelmanage/api.php';
const AUTH_HEADER = {
  headers: {
    'Authorization': 'Basic YWRtaW46cGFzc3dvcmQ=', // admin:password encoded to Base64
    'Content-Type': 'application/json' // Ensure content type is JSON for POST requests
  }
};

const AKASA_API_KEY = 'd57056c77bb1e88221f2f3893a8691af49e6a3855bd3d517628bfdd766c7fa48'; // <--- !!! REPLACE THIS !!!
const AKASA_SENDER_ID = 'Akasa.lk'; // <--- !!! REPLACE THIS !!!

export default function ViewOrder() {
  const [orderItems, setOrderItems] = useState([]);
  const [note, setNote] = useState("");
  const [modalOpen, setModalOpen] = useState(false); // For the note modal
  const [tempNote, setTempNote] = useState("");
  const [pendingDeleteItem, setPendingDeleteItem] = useState(null);
  const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState(false);
  const [confirmCheckoutModalOpen, setConfirmCheckoutModalOpen] = useState(false);
  const [isProcessingOrder, setIsProcessingOrder] = useState(false); // State for checkout loading
  const [showInfoModal, setShowInfoModal] = useState(false); // State for general info/error modal
  const [infoModalMessage, setInfoModalMessage] = useState(""); // Message for info modal
  const [shouldNavigateToMenu, setShouldNavigateToMenu] = useState(false); // New state to control navigation from info modal
  const navigate = useNavigate();

  // Function to handle empty cart logic
  const handleEmptyCart = () => {
    setInfoModalMessage("Your cart is empty. Please add items before checking out.");
    setShowInfoModal(true);
    setShouldNavigateToMenu(true); // Set flag to true for menu navigation
  };

  // Load cart items from localStorage on initial render
  useEffect(() => {
    try {
      const encryptedCart = localStorage.getItem("cart");
      let savedCart = {};
      if (encryptedCart) {
        const decryptedCart = decrypt(encryptedCart);
        if (decryptedCart) {
          savedCart = JSON.parse(decryptedCart);
        }
      }

      const itemsArray = Object.values(savedCart);
      if (itemsArray.length === 0) {
        handleEmptyCart(); // Call empty cart handler on initial load
      }
      setOrderItems(itemsArray);

    } catch (e) {
      console.error("Failed to decrypt or parse cart from localStorage:", e);
      setOrderItems([]);
      handleEmptyCart(); // Call empty cart handler if parsing fails
    }
  }, []);

  // Calculate total items and subtotal
  const totalItems = orderItems.reduce((sum, item) => sum + item.quantity, 0);
  const subtotal = orderItems.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );

  // Handle quantity changes for an item in the cart
  const handleQuantityChange = (id, quantity) => {
    const qty = parseInt(quantity);
    const itemToUpdate = orderItems.find((item) => item.id === id);

    // Use item.maxqty from the localStorage cart data
    const availableStock = itemToUpdate?.maxqty;

    if (itemToUpdate && availableStock !== undefined && qty > availableStock) {
      setInfoModalMessage(`Cannot add ${qty} of ${itemToUpdate.name}. Only ${availableStock} left.`);
      setShowInfoModal(true);
      return;
    }

    if (qty === 0) {
      const itemToDelete = orderItems.find((item) => item.id === id);
      setPendingDeleteItem(itemToDelete);
      setConfirmDeleteModalOpen(true);
      return;
    }

    const updated = orderItems.map((item) =>
      item.id === id ? { ...item, quantity: qty } : item
    );

    setOrderItems(updated);
    // Encrypt and update localStorage to reflect the quantity change
    try {
      const cartString = JSON.stringify(Object.fromEntries(updated.map((i) => [i.id, i])));
      const encryptedCart = encrypt(cartString);
      localStorage.setItem("cart", encryptedCart);
    } catch (e) {
      console.error("Failed to encrypt or save cart to localStorage:", e);
    }
  };

  // Confirm deletion of an item from the cart
  const confirmDelete = () => {
    if (pendingDeleteItem) {
      const updated = orderItems.filter((item) => item.id !== pendingDeleteItem.id);
      setOrderItems(updated);
      // Encrypt and update localStorage after deletion
      try {
        const cartString = JSON.stringify(Object.fromEntries(updated.map((i) => [i.id, i])));
        const encryptedCart = encrypt(cartString);
        localStorage.setItem("cart", encryptedCart);
      } catch (e) {
        console.error("Failed to encrypt or save cart to localStorage:", e);
      }
      setPendingDeleteItem(null);
    }
    setConfirmDeleteModalOpen(false);
  };

  // Cancel deletion of an item
  const cancelDelete = () => {
    setPendingDeleteItem(null);
    setConfirmDeleteModalOpen(false);
  };

  // Truncate long notes for display
  const shortNote = (text) => {
    if (!text) return "";
    const words = text.trim().split(" ");
    return words.length > 6 ? words.slice(0, 6).join(" ") + "..." : text;
  };

  // Handle submission of the note
  const handleNoteSubmit = () => {
    setNote(tempNote.trim());
    setTempNote("");
    setModalOpen(false);
  };

  // Show the checkout confirmation modal
  const handleCheckoutClick = () => {
    if (orderItems.length === 0) {
      handleEmptyCart(); // Call empty cart handler when checkout is clicked
      return;
    }
    setConfirmCheckoutModalOpen(true);
  };



  async function sendSmsInBackground(smstoken) {
    if (!smstoken) {
      console.error('SMS Not Sent: Missing SMS token.');
      return;
    }
  
    const payload = {
      apiKey: AKASA_API_KEY,
      senderId: AKASA_SENDER_ID,
      smstoken: smstoken 
    };
  
    const smsApiEndpoint = 'https://stage.beachcabana.lk/hotelmanage/sendsms.php?param=sendregistersms'; // Relative to your proxy base URL
  
    try {
      await axios.post(smsApiEndpoint, payload, {
        headers: {
          'Content-Type': 'application/json', // Assuming JSON. Adjust if the API expects 'application/x-www-form-urlencoded'
        },
      });
      // SMS sent successfully. No UI update needed.
      // console.log('SMS sent successfully (background task).');
    } catch (err) {
      // Log SMS sending errors to console, but do not show to user
      console.error('Error sending SMS in background:', err);
      if (err.response) {
        console.error('SMS API Error Response (background):', err.response.data);
        console.error('SMS API Error Status (background):', err.response.status);
      } else if (err.request) {
        console.error('SMS Network Error (background): No response from SMS server.');
      } else {
        console.error('SMS Request setup error (background):', err.message);
      }
    }
    // No finally block to set loading or update UI states, as it's background.
  }

  // Process the checkout by sending data to the API
 // In ViewOrder.js, inside the processCheckout function
const processCheckout = async () => {
  setConfirmCheckoutModalOpen(false);
  setIsProcessingOrder(true);

  try {
    // 1. Get the menuOrderToken and currentRegistrationId from cookies
    const encryptedMenuOrderToken = Cookies.get('registrationToken');
    const encryptedCurrentRegistrationToken = Cookies.get('currentRegistrationId');


    if (!encryptedMenuOrderToken || !encryptedCurrentRegistrationToken) {
      throw new Error("Device not registered. Please go back to Menu and register.");
    }

    const menuOrderToken = decrypt(encryptedMenuOrderToken);
    const currentRegistrationId = decrypt(encryptedCurrentRegistrationToken);

    if (!menuOrderToken || !currentRegistrationId) {
      throw new Error("Invalid registration data. Please re-register your device.");
    }

    // 2. Prepare Product Data
    const productData = orderItems.map(item => ({
      item_id: item.id,
      item_name: item.name,
      qty: item.quantity,
      unit_price: item.price,
      items_price: item.price * item.quantity,
      category_id: item.categoryId // Include category_id if needed by API
    }));

    // Calculate total for the order
    const calculatedTotal = productData.reduce((sum, item) => sum + item.items_price, 0);

    // 3. Prepare Token User Info
    const tokenUserInfo = {
      token: menuOrderToken,
      registration_id: currentRegistrationId,
      order_note: note || "",
      total_amount: calculatedTotal,
    };

    // Combine product data and token user info into the payload
    const payload = {
      products: productData,
      user_info: tokenUserInfo
    };

    // Make the POST request to the API
    const response = await axios.post(`${API_BASE_URL}?param=neworder`, payload, AUTH_HEADER);

    if (response.data.code === 200) {


        const smsToken = response.data.data?.smstoken; // Get SMS token from response

        if (smsToken) {
            sendSmsInBackground(smsToken);
        } else {
            console.warn("SMS not sent: Missing SMS token or phone number from registration response.");
        }


      sendSmsInBackground();

      setInfoModalMessage("Order placed successfully!");
      setShowInfoModal(true);
      localStorage.removeItem("cart"); // Clear cart on successful order
      setOrderItems([]); // Clear order items from state
      // Modified navigation to include registration_id
      navigate("/orderprocessing", { 
        state: { 
          orderId: response.data.data.orderid || "success",
          registration_id: currentRegistrationId, // Add registration_id to state
          token: menuOrderToken,
        } 
      });
    } else {
      throw new Error(response.data.message || "Failed to place order.");
    }

  } catch (error) {
    console.error("Error during checkout:", error);
    setInfoModalMessage(`Failed to place order: ${error.message}. Please try again.`);
    setShowInfoModal(true);
  } finally {
    setIsProcessingOrder(false);
  }
};
  return (
    <Layout pageTitle="Checkout">
      <div className="mx-auto py-6 font-sans text-black bg-white min-h-screen flex flex-col relative">
        {/* Header */}
        <header className="flex justify-between items-center mb-4">
          <button
            className="bg-gray-200 rounded-full w-8 h-8 text-2xl flex items-center justify-center hover:bg-gray-300 p-0 pb-[6px] leading-[1]"
            onClick={() => navigate(-1)} // Go back to previous page
            aria-label="Close"
          >
            &times;
          </button>
        </header>

        {/* Subtotal Summary */}
        <div className="flex justify-between text-sm border-t border-b border-gray-300 py-3 mb-4">
          <div>{totalItems} item{totalItems !== 1 ? "s" : ""}</div>
          <div>
            Subtotal: <strong>LKR {subtotal.toLocaleString()}.00</strong>
          </div>
        </div>

        {/* Order Items */}
        <div className="space-y-4 flex-grow overflow-auto pb-32">
          {orderItems.length === 0 && !showInfoModal ? ( // Only show this if cart is truly empty and no info modal is active
            <p className="text-center text-gray-500">Your cart is empty.</p>
          ) : (
            orderItems.map((item) => {
              // Use item.maxqty directly from the cart item
              const maxQuantity = item.maxqty;
              // Generate options up to maxQuantity (inclusive of 0 for removal)
              const quantityOptions = Array.from({ length: maxQuantity + 1 }, (_, i) => i);

              return (
                <div key={item.id} className="flex justify-between items-center">
                  <div className="flex items-center gap-4">
                    <div className="bg-gray-200 rounded-full px-3 py-1">
                      <select
                        className="bg-transparent border-none outline-none text-sm appearance-none focus:outline-none focus:ring-0 text-center"
                        value={item.quantity}
                        onChange={(e) =>
                          handleQuantityChange(item.id, e.target.value)
                        }
                      >
                        {quantityOptions.map((n) => (
                          <option key={n} value={n}>
                            {n}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="font-medium">{item.name}</div>
                  </div>
                  <div className="font-semibold text-right text-sm">
                    LKR {(item.price * item.quantity).toLocaleString()}.00
                  </div>
                </div>
              );
            })
          )}
        </div>

{/* Note Input */}
<div className="mt-6 bg-gray-100 rounded-lg p-3 flex items-center gap-3">
  <input
    type="text"
    placeholder="Utensils, special instructions, etc."
    value={shortNote(note)}
    readOnly
    className="bg-transparent flex-grow text-base border-none outline-none focus:outline-none focus:ring-0"
  />
  <button
    className="w-8 h-8 flex items-center justify-center rounded-full text-xl font-bold text-gray-600 hover:text-gray-800 bg-white shadow"
    onClick={() => setModalOpen(true)}
    aria-label="Add note"
  >
    +
  </button>
</div>

{/* fixed bottom-4 left-4 right-4 bg-black text-white px-6 py-3 rounded-full flex justify-between items-center shadow-md cursor-pointer */}

        {/* Final Subtotal */}
        <div className="flex justify-between font-semibold text-sm border-t border-b border-gray-300 py-3 mt-6">
          <div>Subtotal</div>
          <div><strong>LKR {subtotal.toLocaleString()}.00</strong></div>
        </div>

       {/* Checkout Button - Hidden if cart is empty */}
{orderItems.length > 0 && (
  <button
    onClick={handleCheckoutClick}
    className={`fixed bottom-4 left-4 right-4 bg-black text-white px-6 py-3 text-lg rounded-full shadow-lg hover:bg-gray-900 ${isProcessingOrder ? 'opacity-50 cursor-not-allowed' : ''}`}
    disabled={isProcessingOrder}
  >
    {isProcessingOrder ? "Processing Order..." : "Continue"}
  </button>
)}

        {/* Note Modal */}
        {modalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white w-full max-w-md mx-auto rounded-lg p-6 space-y-4">
              <h2 className="text-lg font-bold">Add a note</h2>
              <textarea
                className="w-full border border-gray-300 rounded p-2 text-sm resize-none"
                rows={4}
                placeholder="Write your note here..."
                value={tempNote}
                onChange={(e) => setTempNote(e.target.value)}
              ></textarea>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setModalOpen(false)}
                  className="px-4 py-2 text-sm bg-gray-200 rounded hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={handleNoteSubmit}
                  className="px-4 py-2 text-sm bg-black text-white rounded hover:bg-gray-900"
                >
                  Submit
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Confirm Delete Item Modal */}
        {confirmDeleteModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white w-full max-w-lg mx-auto rounded-lg shadow p-6">
              <h3 className="text-xl font-bold mb-4 text-gray-800">Remove Product</h3>
              <p className="text-sm text-gray-600 mb-6">
                Are you sure you want to remove <strong>{pendingDeleteItem?.name}</strong> from the cart?
              </p>
              <div className="flex justify-end gap-3">
                
              </div>

              <div class="text-center mt-4">
                <button onClick={confirmDelete} className="px-6 py-2 w-full bg-blue-600 text-white font-semibold rounded bg-red-600 hover:bg-red-700">
                  Remove
                </button>
                
                <button onClick={cancelDelete} className="px-6 py-2 w-full bg-white text-gray-900 font-medium text-sm rounded-lg hover:bg-gray-100 mt-3">
                  Cancel
                  </button>
                
                </div>
            </div>
          </div>
        )}

        {/* Checkout Confirmation Modal */}
        {confirmCheckoutModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="bg-white w-full max-w-md mx-auto rounded-lg shadow p-6">
              <h3 className="text-xl font-bold mb-4 text-gray-800">Confirm Order</h3>
              <p className="text-sm text-gray-600 mb-8">
                Are you sure you want to place this order?
              </p>
              <div className="text-center gap-4 mt-6">
              <button
                  onClick={processCheckout}
                  className={`px-6 py-2 w-full bg-black text-white font-semibold rounded-lg hover:bg-gray-800 ${isProcessingOrder ? 'opacity-50 cursor-not-allowed' : ''}`}
                  disabled={isProcessingOrder}
                >
                  {isProcessingOrder ? "Confirming..." : "Confirm Order"}
                </button>

                <button
                  onClick={() => setConfirmCheckoutModalOpen(false)}
                  className="px-6 py-2 w-full bg-white text-gray-800 font-semibold rounded-lg hover:text-gray-500 mt-3"
                >
                  Cancel
                </button>
              
              </div>
            </div>
          </div>
        )}

        {/* General Info/Error Modal */}
        {showInfoModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white w-full max-w-sm mx-auto rounded-lg shadow-lg p-6 text-center">
              <h3 className="text-xl font-bold mb-4 text-gray-800">Information</h3>
              <p className="text-gray-700 mb-6">{infoModalMessage}</p>
              <button
                onClick={() => {
                  setShowInfoModal(false);
                  if (shouldNavigateToMenu) {
                    navigate("/menu");
                  }
                }}
                className="px-6 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700"
              >
                {shouldNavigateToMenu ? "Go to Menu" : "OK"}
              </button>
            </div>
          </div>
        )}

      </div>
    </Layout>
  );
}
